// Tema por variante da tela CRM Tickets (sunlit / funnel / soft).
// Fonte da verdade: design_handoff_crm_kanban/CRM Tickets.dc.html (Component.V +
// estilos inline por variante). Um único objeto V dirige toda a UI: cores
// primárias, mudas, bolhas, fundos, bordas, raios e tipografia.

import { VARIANT_FONT } from "../../CrmKanban/kanban/tokens";

// Chaves internas: a=sunlit, b=funnel, c=soft (mesma nomenclatura do protótipo).
export const VARIANT_KEY = {
  sunlit: "a",
  funnel: "b",
  soft: "c",
};

// Núcleo cromático (idêntico ao Component.V do design).
const CORE = {
  a: {
    prim: "#F2653A",
    primHover: "#E05528",
    mut: "#9B9486",
    outBg: "#FDE0D2",
    outBd: "none",
    inBg: "#FFFFFF",
    inBd: "none",
    activeBg: "#F8EFDF",
    edge: "transparent",
    activeEdge: "#F2653A",
    track: "#D9CFBB",
  },
  b: {
    prim: "#4A6CF7",
    primHover: "#3A57D8",
    mut: "#98A2B3",
    outBg: "#E3E9FE",
    outBd: "none",
    inBg: "#FFFFFF",
    inBd: "1px solid #E6EAF0",
    activeBg: "#EEF2FE",
    edge: "transparent",
    activeEdge: "#4A6CF7",
    track: "#CBD2DB",
  },
  c: {
    prim: "#7C5CF0",
    primHover: "#6A4AE0",
    mut: "#9A92AB",
    outBg: "#EAE3FC",
    outBd: "none",
    inBg: "#FFFFFF",
    inBd: "none",
    activeBg: "#FFFFFF",
    edge: "transparent",
    activeEdge: "#7C5CF0",
    track: "#D5CEE4",
  },
};

// Cromo + layout (superfícies, bordas, fundos de painel, raios, notas, inputs).
const THEME = {
  a: {
    ...CORE.a,
    key: "a",
    name: "sunlit",
    font: VARIANT_FONT.sunlit,
    pageBg: "#FBF5EC",
    topbarBg: "#F7EFDF",
    topbarBorder: "1px solid #EAE0CC",
    panelBg: "#fff",
    panelBorder: "1px solid #EAE0CC",
    rowBorder: "1px solid #F2EADB",
    divider: "1px solid #F0E7D6",
    chatBg: "#F6EFE3",
    headerBg: "#fff",
    headerBorder: "1px solid #EAE0CC",
    quickBg: "#FBF7EE",
    quickBorder: "1px solid #EFE6D3",
    composerBg: "#fff",
    composerBorder: "1px solid #EAE0CC",
    inputBg: "#FBF5EC",
    inputBorder: "1px solid #E8DFCE",
    inputRadius: 8,
    fieldRadius: 8,
    noteBg: "#FFF7E0",
    noteBorder: "1px solid #F2E4B8",
    noteText: "#6B6456",
    noteTime: "#B7A97E",
    labelMuted: "#A79E8C",
    sublabel: "#8B8577",
    textStrong: "#33302B",
    phoneText: "#9B9486",
    avatarRadius: "50%",
    avatarRowSize: 38,
    tabWeight: 900,
    tabSize: 11.5,
    tabStyle: "underline", // underline | segmented | pills
    subTabStyle: "underline",
    listRowRadius: 0,
    listRowCard: false,
    tagRadius: 999,
    bubbleRadius: 15,
    logoRadius: 9,
    pillRadius: 999,
    btnRadius: 999,
    qrModalRadius: 22,
  },
  b: {
    ...CORE.b,
    key: "b",
    name: "funnel",
    font: VARIANT_FONT.funnel,
    pageBg: "#fff",
    topbarBg: "#fff",
    topbarBorder: "1px solid #E9EDF2",
    panelBg: "#fff",
    panelBorder: "1px solid #E9EDF2",
    rowBorder: "1px solid #F1F4F8",
    divider: "1px solid #EEF1F5",
    chatBg: "#F7F9FC",
    headerBg: "#fff",
    headerBorder: "1px solid #E9EDF2",
    quickBg: "#FAFBFC",
    quickBorder: "1px solid #EEF1F5",
    composerBg: "#fff",
    composerBorder: "1px solid #E9EDF2",
    inputBg: "#F4F6F9",
    inputBorder: "1px solid #E2E7EE",
    inputRadius: 9,
    fieldRadius: 7,
    noteBg: "#FFF8E1",
    noteBorder: "1px solid #F0E3B0",
    noteText: "#5B6472",
    noteTime: "#C0AF77",
    labelMuted: "#98A2B3",
    sublabel: "#7A8496",
    textStrong: "#151B26",
    phoneText: "#98A2B3",
    avatarRadius: 9,
    avatarRowSize: 34,
    tabWeight: 700,
    tabSize: 12,
    tabStyle: "underline",
    subTabStyle: "segmented",
    listRowRadius: 0,
    listRowCard: false,
    tagRadius: 5,
    bubbleRadius: 10,
    logoRadius: 8,
    pillRadius: 7,
    btnRadius: 8,
    qrModalRadius: 14,
  },
  c: {
    ...CORE.c,
    key: "c",
    name: "soft",
    font: VARIANT_FONT.soft,
    pageBg: "#F7F6FB",
    topbarBg: "linear-gradient(115deg,#FFE0CE,#EBDFFE 55%,#D9E8FF)",
    topbarBorder: "none",
    panelBg: "#F7F6FB",
    panelBorder: "1px solid #E8E4F2",
    rowBorder: "none",
    divider: "1px solid #E8E4F2",
    chatBg: "#F1EEF8",
    headerBg: "#fff",
    headerBorder: "1px solid #E8E4F2",
    quickBg: "#F7F6FB",
    quickBorder: "1px solid #E8E4F2",
    composerBg: "#fff",
    composerBorder: "1px solid #E8E4F2",
    inputBg: "#fff",
    inputBorder: "1px solid #E3DDF0",
    inputRadius: 10,
    fieldRadius: 8,
    noteBg: "#FFF7E0",
    noteBorder: "1px solid #F2E4B8",
    noteText: "#5A4B6E",
    noteTime: "#B7A97E",
    labelMuted: "#B0A8C2",
    sublabel: "#8E86A0",
    textStrong: "#322A44",
    phoneText: "#7A6C8A",
    avatarRadius: "50%",
    avatarRowSize: 38,
    tabWeight: 800,
    tabSize: 11,
    tabStyle: "pills",
    subTabStyle: "underline",
    listRowRadius: 14,
    listRowCard: true,
    tagRadius: 999,
    bubbleRadius: 14,
    logoRadius: 9,
    pillRadius: 999,
    btnRadius: 10,
    qrModalRadius: 18,
  },
};

// Retorna o objeto de tema para a variante (default funnel).
export const getTheme = (variant) => THEME[VARIANT_KEY[variant] || "b"];

// Estado da conexão -> cores/label da pill (mesma regra do design).
export const connMeta = (connected) => ({
  text: connected ? "WhatsApp conectado" : "WhatsApp desconectado",
  dot: connected ? "#22C05C" : "#E5484D",
  col: connected ? "#1E7A46" : "#C0392B",
});

export default THEME;
